import * as React from 'react'

import { Icon } from '@factorialco/deprecated-design-system'
import cn from 'classnames/bind'
import _ from 'lodash'

import { observer } from 'mobx-react'

import styles from './index.module.scss'

interface Props {
  items: Array<Record<string, any>>
  selectedIndex: number
  onSelectIndex: (value: number) => void
  renderItem: (index: number) => React.ReactNode
}
interface State {
  rightArrow: boolean
  leftArrow: boolean
  currentPosition: number
}
interface Ref {
  current: React.ElementRef<any>
}
// TODO: check this type after TS migration
// type RefCurrent = React.ElementRef<any>
type Rect = any
interface Direction {
  left: number
  right: number
}

const cx = cn.bind(styles)

class NavigationSlider extends React.Component<Props, State> {
  wrapperRef: Ref
  middleRef: Ref
  fixedHeaderRef: Ref
  items: Array<Record<string, any>> | undefined
  itemsRefs: Ref[]

  constructor(props: Props) {
    super(props)
    this.wrapperRef = React.createRef()
    this.middleRef = React.createRef()
    this.fixedHeaderRef = React.createRef()
    this.itemsRefs = props.items.map(() => React.createRef())
    this.state = {
      rightArrow: false,
      leftArrow: false,
      currentPosition: 0,
    }
  }

  componentDidMount() {
    this.realignSlider()
    window.addEventListener('resize', this.realignSlider)
  }

  componentWillUnmount() {
    window.removeEventListener('resize', this.realignSlider)
  }

  componentDidUpdate(prevProps: Props) {
    if (this.props.items.length !== prevProps.items.length) {
      this.itemsRefs = this.props.items.map(() => React.createRef())
      this.forceUpdate()
    }

    if (this.props.selectedIndex !== prevProps.selectedIndex) {
      this.selectIndex(this.props.selectedIndex)
    }
  }

  realignSlider = () => {
    this.setWrapperStyle(0)
    this.displayRightArrow(0)
    this.displayLeftArrow(0)
  }

  get wrapper(): any {
    return this.wrapperRef.current
  }

  get mostRightRect(): Rect {
    return (_.last(this.itemsRefs) as any).current.getBoundingClientRect()
  }

  get mostLeftRect(): Rect {
    return (this.itemsRefs[0] as any).current.getBoundingClientRect()
  }

  get directions(): Direction {
    return {
      right: -200,
      left: 200,
    }
  }

  get wrapperWidth(): number {
    return this.mostRightRect.right - this.mostLeftRect.left
  }

  get padding(): number {
    return 20
  }

  get middleWidth(): number {
    return (this.middleRef as any).current.offsetWidth
  }

  get middleOverflows(): boolean {
    return this.wrapperWidth > this.middleWidth
  }

  get distanceToEnd(): number {
    const total = this.middleWidth - this.wrapperWidth - this.padding

    return total - this.state.currentPosition
  }

  onClickIndex(index: number) {
    this.props.onSelectIndex(index)
    this.selectIndex(index)
  }

  selectIndex(index: number) {
    if (!this.itemsRefs[index]) return

    const rect = (this.itemsRefs[index] as any).current.getBoundingClientRect()
    const middleRect = (this.middleRef as any).current.getBoundingClientRect()

    const distance = middleRect.left + this.middleWidth / 2 - rect.left - rect.width / 2
    this.move(distance)
  }

  displayRightArrow(amount: number): boolean {
    const middle = (this.middleRef as any).current.getBoundingClientRect()
    const rightLimit = middle.x + middle.width

    if (rightLimit > this.mostRightRect.right + amount || !this.middleOverflows) {
      this.setState({
        rightArrow: false,
      })

      return false
    } else {
      this.setState({
        rightArrow: true,
      })

      return true
    }
  }

  displayLeftArrow(amount: number): boolean {
    const middle = (this.middleRef as any).current.getBoundingClientRect()

    if (middle.x <= this.mostLeftRect.left + amount || !this.middleOverflows) {
      this.setState({
        leftArrow: false,
      })

      return false
    } else {
      this.setState({
        leftArrow: true,
      })

      return true
    }
  }

  setWrapperStyle(newPosition: number) {
    if (this.wrapper) {
      this.wrapper.style = this.wrapper.style || {}
      this.wrapper.style.transform = `translatex(${newPosition}px)`
    }
  }

  move(amount: number) {
    const { currentPosition } = this.state
    let newPosition = currentPosition

    // We don't care about moving if
    // (1) the total width is not big enough or
    // (2) is the first item
    if (!this.middleOverflows || !this.displayLeftArrow(amount)) {
      newPosition = 0 // Don't move to the right if it is the last item
    } else if (!this.displayRightArrow(amount)) {
      newPosition = Math.min(
        this.middleWidth - this.wrapperWidth - this.padding,
        newPosition,
        this.distanceToEnd
      )
    } else {
      newPosition += amount
    }

    this.setState({
      currentPosition: newPosition,
    })
    this.setWrapperStyle(newPosition)
  }

  render() {
    const { items, renderItem } = this.props
    const { leftArrow, rightArrow } = this.state

    return (
      <div className={styles.cardSlider}>
        <div
          className={cx('left', leftArrow && 'visible')}
          onClick={() => this.move(this.directions.left)}
        >
          <div className={cx('arrow', leftArrow && 'visible')}>
            <Icon.Medium icon="arrow-left" color="radicalRed1000" />
          </div>
        </div>
        <div className={styles.middle} ref={this.middleRef as any}>
          <div className={styles.wrapper} ref={this.wrapperRef as any}>
            {items.map((_item: any, index: number) => (
              <div
                className={styles.itemWrapper}
                key={index}
                ref={this.itemsRefs[index] as any}
                onClick={() => this.onClickIndex(index)}
              >
                {renderItem(index)}
              </div>
            ))}
          </div>
        </div>
        <div
          className={cx('right', rightArrow && 'visible')}
          onClick={() => rightArrow && this.move(this.directions.right)}
        >
          <div className={cx('arrow', rightArrow && 'visible')}>
            <Icon.Medium icon="arrow-right" color="radicalRed1000" />
          </div>
        </div>
      </div>
    )
  }
}

export default observer(NavigationSlider)
